import { SseEventSchemas, type SseEvent as SharedSseEvent } from "@hamlak/shared";

/** shared의 이벤트 + 서버가 준 `id`(마지막 이벤트 추적용). */
export type SseEvent = SharedSseEvent & { id?: string };
export type SseResult = { completed: boolean };

type EventName = keyof typeof SseEventSchemas;
const isKnownEvent = (name: string): name is EventName => Object.hasOwn(SseEventSchemas, name);

function abortError(): DOMException {
  return new DOMException("The operation was aborted.", "AbortError");
}

/**
 * `text/event-stream` Response를 읽어 이벤트마다 onEvent를 부른다. EventSource는 쓰지 않는다(커스텀 헤더 불가).
 * - 청크 경계·멀티바이트 안전, 멀티라인 data는 `\n`으로 잇는다. `: ping` 코멘트·`retry` 무시.
 * - 알 수 없는 event는 건너뛴다. 아는 이벤트는 shared zod 스키마로 검증(실패는 throw).
 * - `done` 이벤트를 받으면 completed=true, 없이 닫히면 false. abort되면 AbortError.
 */
export async function readSse(res: Response, onEvent: (e: SseEvent) => void, signal?: AbortSignal): Promise<SseResult> {
  if (!res.body) return { completed: false };
  if (signal?.aborted) throw abortError();

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let eventName = "";
  let eventId: string | undefined;
  let dataLines: string[] = [];
  let completed = false;

  const onAbort = () => void reader.cancel().catch(() => undefined);
  signal?.addEventListener("abort", onAbort, { once: true });

  const dispatch = () => {
    const name = eventName;
    const id = eventId;
    const lines = dataLines;
    eventName = "";
    eventId = undefined;
    dataLines = [];
    if (lines.length === 0 || !isKnownEvent(name)) return;
    const data: unknown = JSON.parse(lines.join("\n"));
    const parsed = SseEventSchemas[name].parse(data);
    const ev = (id === undefined ? { event: name, data: parsed } : { event: name, id, data: parsed }) as SseEvent;
    if (name === "done") completed = true;
    onEvent(ev);
  };

  const handleLine = (raw: string) => {
    const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    if (line === "") return dispatch();
    if (line.startsWith(":")) return;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") eventName = value;
    else if (field === "data") dataLines.push(value);
    else if (field === "id") eventId = value;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (signal?.aborted) throw abortError();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf("\n")) !== -1) {
        handleLine(buffer.slice(0, nl));
        buffer = buffer.slice(nl + 1);
      }
    }
    buffer += decoder.decode();
    if (buffer.length > 0) handleLine(buffer);
    dispatch();
    return { completed };
  } finally {
    signal?.removeEventListener("abort", onAbort);
    reader.releaseLock();
  }
}
